import { zodResolver } from "@hookform/resolvers/zod";
import { CalendarIcon } from "lucide-react";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { TimeInput } from "@/components/ui/time-input";
import { TimezoneService, timezoneUtils } from "@/lib/timezone";
import { cn } from "@/lib/utils";
import { useHolidays } from "../hooks";
import { useHolidayByIdQuery } from "../hooks/use-holiday-query";
import { useHolidayUIStore } from "../store/use-holiday-ui-store";
import type { HolidayFormData } from "../types";
import { countMatchingHolidays } from "../utils/count-matching-holidays";
import { shiftHolidayToNextDay } from "../utils/shift-holiday-to-next-day";

// Helper function to detect if a holiday is set as "all day"
const isHolidayAllDay = (start: DateTime, end: DateTime): boolean => {
  // Check if start is at beginning of day (00:00:00)
  const isStartOfDay = start.hour === 0 && start.minute === 0 && start.second === 0;

  // Check if end is at end of day (23:59:59)
  const isEndOfDay = end.hour === 23 && end.minute === 59 && end.second === 59;

  return isStartOfDay && isEndOfDay;
};

// Form validation schema - will be updated to use t() function in component
const createHolidaySchema = (t: any) =>
  z
    .object({
      name: z
        .string()
        .min(1, t("validation.holidayNameRequired"))
        .max(100, t("validation.holidayNameMaxLength")),
      message: z.string().max(500, t("validation.holidayMessageMaxLength")).optional(),
      startDateTime: z.custom<DateTime>(
        (val) => val instanceof DateTime,
        t("validation.startDateRequired")
      ),
      endDateTime: z.custom<DateTime>(
        (val) => val instanceof DateTime,
        t("validation.endDateRequired")
      ),
      blockOnlineBooking: z.boolean(),
    })
    .refine((data) => data.startDateTime < data.endDateTime, {
      message: t("validation.endDateAfterStart"),
      path: ["endDateTime"],
    });

export function HolidayForm() {
  const { t } = useTranslation("settings");

  const {
    isCreateDialogOpen,
    isEditDialogOpen,
    editingHolidayId,
    duplicatingHolidayId,
    closeCreateDialog,
    closeEditDialog,
  } = useHolidayUIStore();

  const { holidays, createHoliday, updateHoliday, creating, updating } = useHolidays();

  // A create that matches existing holidays waits here for the user to confirm
  const [pendingCreate, setPendingCreate] = useState<{
    data: HolidayFormData;
    count: number;
  } | null>(null);

  const isOpen = isCreateDialogOpen || isEditDialogOpen;
  const isEditing = isEditDialogOpen && !!editingHolidayId;
  const isDuplicating = isCreateDialogOpen && !!duplicatingHolidayId;
  const sourceHolidayId = isEditing ? editingHolidayId : duplicatingHolidayId;

  // Fetch holiday data for editing or duplicating
  const { data: holidayData, loading: holidayLoading } = useHolidayByIdQuery(
    isEditing || isDuplicating ? (sourceHolidayId ?? undefined) : undefined
  );

  const isSourceLoading = (isEditing || isDuplicating) && holidayLoading;
  const isDuplicateSourceMissing = isDuplicating && !holidayLoading && !holidayData?.getHoliday;
  const isLoading = creating || updating || isSourceLoading;

  // All day toggle state
  const [isAllDay, setIsAllDay] = useState(true);

  // Time states for specific time mode
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("17:00");

  // Form setup
  const form = useForm<HolidayFormData>({
    resolver: zodResolver(createHolidaySchema(t)),
    defaultValues: {
      name: "",
      message: "",
      startDateTime: timezoneUtils.startOfDay(DateTime.now()),
      endDateTime: timezoneUtils.endOfDay(DateTime.now()),
      blockOnlineBooking: true,
    },
  });

  // Reset form when holiday data loads for editing or duplicating
  useEffect(() => {
    if ((isEditing || isDuplicating) && holidayData?.getHoliday) {
      const holiday = holidayData.getHoliday;
      const savedStart = timezoneUtils.fromISO(holiday.startDateTime);
      const savedEnd = timezoneUtils.fromISO(holiday.endDateTime);
      const { startDateTime, endDateTime } = isDuplicating
        ? shiftHolidayToNextDay(savedStart, savedEnd)
        : { startDateTime: savedStart, endDateTime: savedEnd };

      // Detect if the holiday was set as all day
      const detectedAllDay = isHolidayAllDay(startDateTime, endDateTime);
      setIsAllDay(detectedAllDay);

      // Set time values if not all day
      if (!detectedAllDay) {
        setStartTime(startDateTime.toFormat("HH:mm"));
        setEndTime(endDateTime.toFormat("HH:mm"));
      }

      form.reset({
        name: holiday.name,
        message: holiday.message || "",
        startDateTime,
        endDateTime,
        blockOnlineBooking: holiday.blockOnlineBooking,
      });
    } else if (!isEditing && !isDuplicating) {
      // Reset for create mode
      setIsAllDay(true);
      setStartTime("09:00");
      setEndTime("17:00");

      form.reset({
        name: "",
        message: "",
        startDateTime: timezoneUtils.startOfDay(DateTime.now()),
        endDateTime: timezoneUtils.endOfDay(DateTime.now()),
        blockOnlineBooking: true,
      });
    }
  }, [isEditing, isDuplicating, holidayData, form]);

  // Handle all day toggle
  const handleAllDayToggle = (checked: boolean) => {
    setIsAllDay(checked);

    const currentStart = form.getValues("startDateTime");
    const currentEnd = form.getValues("endDateTime");

    if (checked) {
      // Switch to all day mode - set to start/end of day
      form.setValue("startDateTime", timezoneUtils.startOfDay(currentStart));
      form.setValue("endDateTime", timezoneUtils.endOfDay(currentEnd));
    } else {
      // Switch to specific time mode - apply stored times
      const [startHour, startMinute] = startTime.split(":").map(Number);
      const [endHour, endMinute] = endTime.split(":").map(Number);

      form.setValue(
        "startDateTime",
        currentStart.set({ hour: startHour, minute: startMinute, second: 0, millisecond: 0 })
      );
      form.setValue(
        "endDateTime",
        currentEnd.set({ hour: endHour, minute: endMinute, second: 0, millisecond: 0 })
      );
    }
  };

  // Handle start date change
  const handleStartDateChange = (date: Date | undefined) => {
    if (!date) return;

    // Convert JS Date to DateTime in salon timezone, preserving the local date
    const newStartDate = DateTime.fromObject(
      {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate(),
      },
      { zone: TimezoneService.getCurrentSalonTimezone() }
    );

    if (isAllDay) {
      form.setValue("startDateTime", timezoneUtils.startOfDay(newStartDate));
    } else {
      const [hours, minutes] = startTime.split(":").map(Number);
      form.setValue(
        "startDateTime",
        newStartDate.set({ hour: hours, minute: minutes, second: 0, millisecond: 0 })
      );
    }
  };

  // Handle end date change
  const handleEndDateChange = (date: Date | undefined) => {
    if (!date) return;

    // Convert JS Date to DateTime in salon timezone, preserving the local date
    const newEndDate = DateTime.fromObject(
      {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate(),
      },
      { zone: TimezoneService.getCurrentSalonTimezone() }
    );

    if (isAllDay) {
      form.setValue("endDateTime", timezoneUtils.endOfDay(newEndDate));
    } else {
      const [hours, minutes] = endTime.split(":").map(Number);
      form.setValue(
        "endDateTime",
        newEndDate.set({ hour: hours, minute: minutes, second: 0, millisecond: 0 })
      );
    }
  };

  // Handle start time change
  const handleStartTimeChange = (time: string) => {
    setStartTime(time);

    const currentStart = form.getValues("startDateTime");
    const [hours, minutes] = time.split(":").map(Number);

    form.setValue(
      "startDateTime",
      currentStart.set({ hour: hours, minute: minutes, second: 0, millisecond: 0 })
    );
  };

  // Handle end time change
  const handleEndTimeChange = (time: string) => {
    setEndTime(time);

    const currentEnd = form.getValues("endDateTime");
    const [hours, minutes] = time.split(":").map(Number);

    form.setValue(
      "endDateTime",
      currentEnd.set({ hour: hours, minute: minutes, second: 0, millisecond: 0 })
    );
  };

  const handleClose = () => {
    if (isCreateDialogOpen) {
      closeCreateDialog();
    } else {
      closeEditDialog();
    }
    form.reset();
  };

  const createNew = async (data: HolidayFormData) => {
    if (!isDuplicating) {
      await createHoliday(data);
      return;
    }
    // Never fall back to a plain create: the copy would lose the source's scope and salons
    const source = holidayData?.getHoliday;
    if (!source) return;
    await createHoliday(data, {
      scope: source.scope,
      salonIds: source.holidaySalons?.map((holidaySalon) => holidaySalon.salonId) ?? [],
    });
  };

  const onSubmit = async (data: HolidayFormData) => {
    if (!isEditing) {
      const count = countMatchingHolidays(holidays, data);
      if (count > 0) {
        setPendingCreate({ data, count });
        return;
      }
    }

    try {
      if (isEditing && editingHolidayId) {
        await updateHoliday(editingHolidayId, data);
      } else {
        await createNew(data);
      }
      handleClose();
    } catch {
      // Error is handled by the mutation hook
    }
  };

  const confirmPendingCreate = async () => {
    if (!pendingCreate) return;
    const { data } = pendingCreate;
    setPendingCreate(null);
    try {
      await createNew(data);
      handleClose();
    } catch {
      // Error is handled by the mutation hook
    }
  };

  const createTitle = isDuplicating ? t("dialogs.duplicateHoliday") : t("dialogs.createHoliday");
  const title = isEditing ? t("dialogs.editHoliday") : createTitle;
  const submitText = isEditing ? t("actions.update") : t("actions.add");

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        {isSourceLoading ? (
          <div className="flex items-center justify-center py-8">
            <Spinner size="lg" />
          </div>
        ) : isDuplicateSourceMissing ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {t("holidays.duplicateSourceNotFound")}
          </p>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("holidays.holidayName")}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={t("placeholders.holidayDescription")}
                        disabled={isLoading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="message"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("labels.message")} ({t("labels.optional")})
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder={t("placeholders.optionalMessage")}
                        disabled={isLoading}
                        rows={3}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* All Day Checkbox */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="allDay"
                  checked={isAllDay}
                  onCheckedChange={handleAllDayToggle}
                  disabled={isLoading}
                />
                <label
                  htmlFor="allDay"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  {t("labels.allDay")}
                </label>
              </div>

              {/* Start Date/Time */}
              <div className="space-y-2">
                <FormLabel>{t("labels.startDateTime")}</FormLabel>
                <div className={cn("grid gap-4", isAllDay ? "grid-cols-1" : "grid-cols-2")}>
                  <FormField
                    control={form.control}
                    name="startDateTime"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant="outline"
                                className={cn(
                                  "w-full pl-3 text-left font-normal",
                                  !field.value && "text-muted-foreground"
                                )}
                                disabled={isLoading}
                              >
                                {field.value ? (
                                  field.value.toFormat("MMM dd, yyyy")
                                ) : (
                                  <span>Pick a date</span>
                                )}
                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="!w-[350px] p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={
                                field.value
                                  ? new Date(
                                      field.value.year,
                                      field.value.month - 1,
                                      field.value.day
                                    )
                                  : undefined
                              }
                              onSelect={handleStartDateChange}
                              disabled={isLoading}
                              initialFocus
                              className="w-full [--cell-size:2.5rem]"
                              classNames={{
                                // Explicit row height (= cell width from the 350px popover):
                                // WebKit under-computes row height from aspect-square cells,
                                // leaving the popover background short of the last rows.
                                week: "mt-2 flex h-[calc(326px/7)] w-full",
                              }}
                            />
                          </PopoverContent>
                        </Popover>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {!isAllDay && (
                    <FormItem className="flex flex-col">
                      <FormControl>
                        <TimeInput
                          value={startTime}
                          onChange={handleStartTimeChange}
                          disabled={isLoading}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                </div>
              </div>

              {/* End Date/Time */}
              <div className="space-y-2">
                <FormLabel>{t("labels.endDateTime")}</FormLabel>
                <div className={cn("grid gap-4", isAllDay ? "grid-cols-1" : "grid-cols-2")}>
                  <FormField
                    control={form.control}
                    name="endDateTime"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant="outline"
                                className={cn(
                                  "w-full pl-3 text-left font-normal",
                                  !field.value && "text-muted-foreground"
                                )}
                                disabled={isLoading}
                              >
                                {field.value ? (
                                  field.value.toFormat("MMM dd, yyyy")
                                ) : (
                                  <span>Pick a date</span>
                                )}
                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="!w-[350px] p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={
                                field.value
                                  ? new Date(
                                      field.value.year,
                                      field.value.month - 1,
                                      field.value.day
                                    )
                                  : undefined
                              }
                              onSelect={handleEndDateChange}
                              disabled={isLoading}
                              initialFocus
                              className="w-full [--cell-size:2.5rem]"
                              classNames={{
                                // Explicit row height (= cell width from the 350px popover):
                                // WebKit under-computes row height from aspect-square cells,
                                // leaving the popover background short of the last rows.
                                week: "mt-2 flex h-[calc(326px/7)] w-full",
                              }}
                            />
                          </PopoverContent>
                        </Popover>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {!isAllDay && (
                    <FormItem className="flex flex-col">
                      <FormControl>
                        <TimeInput
                          value={endTime}
                          onChange={handleEndTimeChange}
                          disabled={isLoading}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                </div>
              </div>

              <FormField
                control={form.control}
                name="blockOnlineBooking"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={(checked) => field.onChange(checked === true)}
                        disabled={isLoading}
                      />
                    </FormControl>
                    <FormLabel className="cursor-pointer">
                      {t("labels.blockOnlineBooking")}
                    </FormLabel>
                  </FormItem>
                )}
              />

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={handleClose} disabled={isLoading}>
                  {t("common.cancel")}
                </Button>
                <Button type="submit" disabled={isLoading}>
                  {isLoading && <Spinner className="w-4 h-4 mr-2" />}
                  {submitText}
                </Button>
              </div>
            </form>
          </Form>
        )}
      </DialogContent>

      <AlertDialog
        open={!!pendingCreate}
        onOpenChange={(open) => {
          if (!open) setPendingCreate(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("holidays.duplicateWarning.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("holidays.duplicateWarning.description", { count: pendingCreate?.count ?? 0 })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmPendingCreate}>
              {t("holidays.duplicateWarning.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
